import { useMemo, useState } from "react";
import { videos, categories } from "@/mocks/videos";

interface VideoLibraryProps {
  keyword: string;
  activeCategory: string;
  onCategoryChange: (category: string) => void;
  onClearKeyword: () => void;
}

export default function VideoLibrary({
  keyword,
  activeCategory,
  onCategoryChange,
  onClearKeyword,
}: VideoLibraryProps) {
  const [localKeyword, setLocalKeyword] = useState(keyword);
  const effectiveKeyword = keyword || localKeyword;

  const filtered = useMemo(() => {
    return videos.filter((v) => {
      const matchCat = activeCategory === "全部" || v.category === activeCategory;
      const kw = effectiveKeyword.trim();
      const matchKw =
        kw === "" ||
        v.title.includes(kw) ||
        v.description.includes(kw) ||
        v.category.includes(kw);
      return matchCat && matchKw;
    });
  }, [activeCategory, effectiveKeyword]);

  const submit = () => {
    onClearKeyword();
    setLocalKeyword(localKeyword.trim());
  };

  return (
    <section id="videos" className="bg-background-50 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
              <i className="ri-video-line"></i>
              全部视频
            </p>
            <h2 className="mt-3 font-heading text-2xl text-foreground-950 md:text-3xl">
              最新吃瓜视频资源
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              共 {filtered.length} 个视频，即点即看，无需安装任何插件。
            </p>
          </div>

          <div className="flex w-full items-center gap-2 rounded-lg bg-background-100 px-3 ring-1 ring-background-200 md:w-72">
            <i className="ri-search-line text-foreground-400"></i>
            <input
              type="text"
              value={localKeyword}
              onChange={(e) => setLocalKeyword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="在视频库中搜索..."
              className="h-10 w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onCategoryChange(c)}
              className={`inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors ${
                activeCategory === c
                  ? "bg-primary-500 text-background-50"
                  : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10 flex flex-col items-center gap-3 rounded-lg bg-background-100 py-16 text-center ring-1 ring-background-200">
            <i className="ri-search-line text-4xl text-foreground-300"></i>
            <p className="text-sm text-foreground-600">没有找到相关视频，换个关键词试试吧。</p>
            <button
              type="button"
              onClick={() => {
                onCategoryChange("全部");
                setLocalKeyword("");
                onClearKeyword();
              }}
              className="rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((v) => (
              <article
                key={v.id}
                data-product-shop=""
                className="group overflow-hidden rounded-lg bg-background-100 ring-1 ring-background-200 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="relative w-full">
                  <img
                    src={v.cover}
                    alt={v.title}
                    title={`吃瓜视频 ${v.title}`}
                    className="aspect-video w-full object-cover"
                  />
                  <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-md bg-foreground-950/70 px-2 py-0.5 text-xs text-background-50">
                    <i className="ri-time-line"></i>
                    {v.duration}
                  </span>
                  <div className="absolute inset-0 flex items-center justify-center bg-foreground-950/30 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      type="button"
                      aria-label={`播放 ${v.title}`}
                      className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-background-50"
                    >
                      <i className="ri-play-fill text-xl"></i>
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <span className="inline-flex rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-800">
                    {v.category}
                  </span>
                  <h3 className="mt-2 line-clamp-2 text-sm font-medium text-foreground-950">
                    {v.title}
                  </h3>
                  <div className="mt-3 flex items-center justify-between text-xs text-foreground-600">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {v.views}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {v.date}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}